import { SixDots } from '@/assets/icons/six-dots';
import { Icon, Icons } from '@/components/icon';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Combobox,
  ComboboxEmpty,
  ComboboxGroup,
  ComboboxItem,
  type Option,
} from '@/components/ui/combobox';
import { CountryFlag } from '@/components/ui/country-flag';
import { Button } from '@/components/ui/design-system';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
} from '@/components/ui/dialog';
import { Popover, PopoverContent, PopoverScrollArea } from '@/components/ui/popover';
import { P3 } from '@/components/ui/text';
import { useCanCall } from '@/hooks/calls/use-can-call';
import { useCopyToClipboard } from '@/hooks/use-copy-to-clipboard';
import { useTranslation } from '@/hooks/use-translation';
import { countries } from '@/lib/countries';
import { getNumberAndCountryCode, parsePhoneNumberData } from '@/lib/phone-number';
import { cn, reorder } from '@/lib/utils';
import { useAppCallActions } from '@/providers/app-call-actions/use-app-call-actions';
import { useCallBar } from '@/providers/call-bar/call-bar-provider';
import { useCallCapabilities } from '@/providers/call-capabilities/use-call-capabilities';
import { useLines } from '@/providers/lines/use-lines';
import { ANALYTICS_EVENTS, analytics } from '@/services/analytics';
import { DragDropContext, Draggable, type DropResult, Droppable } from '@hello-pangea/dnd';
import { PopoverAnchor } from '@radix-ui/react-popover';
import { AsYouType, type CountryCode, getExampleNumber } from 'libphonenumber-js';
import examples from 'libphonenumber-js/mobile/examples';
import * as React from 'react';
import { toast } from 'sonner';
import { TableCell } from '../table';

// Build country options from the countries object for the combobox
const countryOptions: (Option & { code: CountryCode; dialCode: string })[] = Object.values(
  countries,
).map((country) => ({
  value: country.code,
  label: country.country,
  code: country.code,
  dialCode: country.num,
}));

// Generate a placeholder mask for a country's phone format
function getPhonePlaceholder(countryCode: CountryCode): string {
  const exampleNumber = getExampleNumber(countryCode, examples);
  if (!exampleNumber) {
    return '__ __ __ __ __';
  }

  // Format nationally (without country code)
  const formatted = exampleNumber.formatNational();
  // Replace digits with underscores to create the mask
  return formatted.replace(/\d/g, '_');
}

const CountryCombobox = React.forwardRef<
  HTMLButtonElement,
  {
    value: CountryCode;
    onValueChange: (value: CountryCode) => void;
    onNavigateUp?: () => void;
  }
>(function CountryCombobox({ value, onValueChange, onNavigateUp }, ref) {
  const { t } = useTranslation('desktop_ui');
  const [open, setOpen] = React.useState(false);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      setOpen(true);
    } else if (e.key === 'ArrowUp' && onNavigateUp) {
      e.preventDefault();
      e.stopPropagation();
      onNavigateUp();
    } else if (e.key === 'ArrowDown') {
      // Open the combobox when pressing ArrowDown
      e.preventDefault();
      e.stopPropagation();
      setOpen(true);
    }
  };

  return (
    <Combobox
      options={countryOptions}
      value={value}
      open={open}
      onOpenChange={setOpen}
      onValueChange={(v) => {
        if (v && countries[v as CountryCode]) {
          onValueChange(v as CountryCode);
        }
      }}
      contentClassName="z-popover w-[240px]"
      trigger={
        <button
          ref={ref}
          type="button"
          onKeyDown={handleKeyDown}
          className="flex items-center gap-0.5 text-body-14 cursor-pointer rounded-md p-1 hover:bg-primary-10 outline-none focus:ring-2 focus:ring-primary-40"
        >
          <span className="text-primary-100">
            <P3>{value}</P3>
          </span>
          <Icon name={Icons.ChevronDown} className="text-body-12 text-primary-50" />
        </button>
      }
    >
      {({ filteredOptions }) => (
        <>
          <ComboboxEmpty>{t('components.contacts.table.noCountryFound')}</ComboboxEmpty>
          <ComboboxGroup>
            {filteredOptions.map((option) => {
              const countryOption = option as (typeof countryOptions)[number];
              return (
                <ComboboxItem key={countryOption.value} option={option}>
                  {() => (
                    <>
                      <span className="text-primary-80">{countryOption.label}</span>
                      <span className="text-primary-40">{countryOption.dialCode}</span>
                    </>
                  )}
                </ComboboxItem>
              );
            })}
          </ComboboxGroup>
        </>
      )}
    </Combobox>
  );
});

function PhoneInput({
  countryCode,
  onCountryChange,
  value,
  onChange,
  onSubmit,
  countryTriggerRef,
  onCountryNavigateUp,
  inputRef: externalInputRef,
}: {
  countryCode: CountryCode;
  onCountryChange: (country: CountryCode) => void;
  value: string;
  onChange: (value: string) => void;
  onSubmit?: (fullNumber: string) => void;
  countryTriggerRef?: React.RefObject<HTMLButtonElement | null>;
  onCountryNavigateUp?: () => void;
  inputRef?: React.RefObject<HTMLInputElement | null>;
}) {
  const internalInputRef = React.useRef<HTMLInputElement>(null);
  const inputRef = externalInputRef ?? internalInputRef;
  const country = countries[countryCode];

  const formatValue = (digits: string) => {
    if (!digits) {
      return '';
    }
    const formatter = new AsYouType(countryCode);
    const formatted = formatter.input(digits);
    const dialCode = country.num.replace('+', '');
    if (formatted.startsWith('+') && formatted.includes(dialCode)) {
      return formatted.slice(formatted.indexOf(dialCode) + dialCode.length).trim();
    }
    return formatted;
  };

  const formattedValue = formatValue(value);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const digitsOnly = e.target.value.replace(/\D/g, '');
    onChange(digitsOnly);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && value && onSubmit) {
      e.preventDefault();
      e.stopPropagation();
      const fullNumber = `${country.num}${value}`;
      onSubmit(fullNumber);
      onChange('');
      return;
    }

    // Handle backspace over formatting characters
    if (e.key === 'Backspace') {
      const input = e.currentTarget;
      const cursorPos = input.selectionStart ?? 0;
      const selectionEnd = input.selectionEnd ?? 0;

      // If there's a selection, let the default behavior handle it
      if (cursorPos !== selectionEnd) {
        return;
      }

      // If cursor is at the start, nothing to delete
      if (cursorPos === 0) {
        return;
      }

      // Check if the character before cursor is a non-digit (formatting character)
      const charBeforeCursor = formattedValue[cursorPos - 1];
      if (charBeforeCursor && !/\d/.test(charBeforeCursor)) {
        e.preventDefault();
        // Remove the last digit from the raw value
        if (value.length > 0) {
          // Find which digit we should remove based on cursor position
          // Count digits before cursor position
          let digitCount = 0;
          for (let i = 0; i < cursorPos; i++) {
            const char = formattedValue[i];
            if (char && /\d/.test(char)) {
              digitCount++;
            }
          }
          // Remove the digit at that position (or the last one if we're after all digits)
          const digitIndex = Math.max(0, digitCount - 1);
          const newValue = value.slice(0, digitIndex) + value.slice(digitIndex + 1);
          onChange(newValue);
        }
      }
    }
  };

  const handleCountryChange = (country: CountryCode) => {
    onCountryChange(country);
    // Focus input after country selection
    requestAnimationFrame(() => {
      inputRef.current?.focus();
    });
  };

  return (
    <div className="flex items-center gap-1">
      <CountryCombobox
        ref={countryTriggerRef}
        value={countryCode}
        onValueChange={handleCountryChange}
        onNavigateUp={onCountryNavigateUp}
      />
      <div className="flex items-center gap-1 text-body-12">
        <span className="text-primary-60 text-s">{country.num}</span>
        <div className="relative inline-flex min-w-[100px]">
          <input
            ref={inputRef}
            type="text"
            inputMode="numeric"
            value={formattedValue}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            placeholder={getPhonePlaceholder(countryCode)}
            className="w-full caret-highlight-default bg-transparent text-primary-100 outline-none font-medium text-s placeholder:text-primary-60"
          />
        </div>
      </div>
    </div>
  );
}

export const CALL_CONFIRMATION_DISMISSED_KEY = 'call_confirmation_dismissed';

function PopoverCopyButton({ phoneNumber }: { phoneNumber: string }) {
  const { t } = useTranslation('desktop_ui');
  const { copied, copy } = useCopyToClipboard({
    onError: () => toast.error(t('components.contacts.table.copyFailed')),
  });

  const handleKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === 'Tab' && !e.shiftKey) {
      e.preventDefault();
      const binButton = e.currentTarget.nextElementSibling as HTMLElement | null;
      if (binButton) {
        binButton.focus();
        return;
      }
      const wrapper = e.currentTarget.parentElement;
      const row = wrapper?.parentElement;
      const nextRow = row?.nextElementSibling;
      if (nextRow) {
        const nextPhone = nextRow.querySelector('span button') as HTMLElement | null;
        nextPhone?.focus();
      }
    } else if (e.key === 'Tab' && e.shiftKey) {
      e.preventDefault();
      const wrapper = e.currentTarget.parentElement;
      const phoneSpan = wrapper?.previousElementSibling as HTMLElement | null;
      const phone = phoneSpan?.querySelector('button') as HTMLElement | null;
      phone?.focus();
    }
  };

  return (
    <button
      type="button"
      tabIndex={0}
      className="group/copy cursor-pointer p-1 text-primary-40 opacity-0 outline-none transition-opacity hover:text-primary-100 hover:opacity-100 focus:opacity-100 group-hover/row:opacity-100"
      aria-label={t('components.contacts.table.copyPhoneNumber')}
      onClick={() => copy(phoneNumber)}
      onKeyDown={handleKeyDown}
    >
      <span className="flex rounded-xs group-focus/copy:ring-2 group-focus/copy:ring-primary-40">
        <Icon name={copied ? Icons.Check : Icons.Copy} className="text-s" />
      </span>
    </button>
  );
}

export function Phone({
  phoneNumber,
  tabIndex,
  contactId,
  contactName,
  onRequestCallConfirm,
  hideCopy,
}: {
  phoneNumber: string;
  tabIndex?: number;
  contactId?: string;
  contactName?: string;
  onRequestCallConfirm?: (startCall: () => void, number: string) => void;
  /** Hide the inline copy-on-hover chip. Used inside the phone popover
   *  where a sibling copy button already exists. */
  hideCopy?: boolean;
}) {
  const { t } = useTranslation('desktop_ui');
  const { number, countryCode } = getNumberAndCountryCode(phoneNumber);
  const { setInput: setCallBarInput } = useCallBar();
  const { call } = useAppCallActions();
  const { currentLine } = useLines();
  const { canCall } = useCanCall();
  const { addFailedToCall } = useCallCapabilities();

  const [showInlineDialog, setShowInlineDialog] = React.useState(false);
  const { copied, copy } = useCopyToClipboard({
    onError: () => toast.error(t('components.contacts.table.copyFailed')),
  });

  const handleCopy = (e: React.MouseEvent) => {
    e.stopPropagation();
    copy(phoneNumber);
  };

  const updateCallBarInput = () => {
    if (contactId && contactName) {
      setCallBarInput({
        type: 'contact',
        contactId,
        number: phoneNumber,
        name: contactName,
      });
    } else {
      setCallBarInput({ type: 'string', value: phoneNumber });
    }
  };

  const startCall = async () => {
    updateCallBarInput();
    const phoneNumberData = parsePhoneNumberData(phoneNumber);
    if (!phoneNumberData) {
      return;
    }
    if (canCall.ok) {
      analytics.track(ANALYTICS_EVENTS.CALL.STARTED, { entry_point: 'phone_link' });
      await call(currentLine, phoneNumberData, undefined, contactId);
    } else {
      addFailedToCall(canCall.reason);
    }
  };

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (localStorage.getItem(CALL_CONFIRMATION_DISMISSED_KEY) === 'true') {
      startCall();
    } else if (onRequestCallConfirm) {
      onRequestCallConfirm(startCall, number);
    } else {
      setShowInlineDialog(true);
    }
  };

  const handleStartCall = () => {
    setShowInlineDialog(false);
    startCall();
  };

  const handleCancel = () => {
    setShowInlineDialog(false);
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    // Prevent the button from stealing focus, but focus the parent cell
    e.preventDefault();
    const cell = (e.target as HTMLElement).closest('[data-cell-row]') as HTMLElement | null;
    cell?.focus();
  };

  return (
    <>
      <div className="group/phone relative flex flex-none rounded-xs cursor-pointer hover:bg-primary-5 focus-within:bg-primary-5">
        <button
          type="button"
          onClick={handleClick}
          onMouseDown={handleMouseDown}
          tabIndex={tabIndex}
          className="flex flex-1 flex-col px-1 rounded-xs outline-none"
        >
          <div className="flex max-h-5 items-center gap-1">
            <CountryFlag countryCode={countryCode as CountryCode | undefined} size="s" />
            <P3 className="text-highlight-default">{number}</P3>
          </div>
          <span className="h-px w-full bg-primary-10 group-hover/phone:opacity-0" />
        </button>
        {!hideCopy && (
          <div className="pointer-events-none absolute inset-y-0 right-0 flex items-stretch opacity-0 transition-opacity duration-150 ease-out group-hover/phone:pointer-events-auto group-hover/phone:opacity-100">
            <div className="w-3 bg-gradient-to-l from-primary-5 to-transparent" />
            <button
              type="button"
              onClick={handleCopy}
              onMouseDown={(e) => e.preventDefault()}
              aria-label={t('components.contacts.table.copyPhoneNumber')}
              className="flex aspect-square items-center justify-center rounded-xs bg-primary-5 cursor-pointer outline-none hover:bg-primary-10 focus:ring-2 focus:ring-primary-40"
            >
              <Icon
                name={copied ? Icons.Check : Icons.Copy}
                className="text-s text-highlight-default"
              />
            </button>
          </div>
        )}
      </div>
      {!onRequestCallConfirm && (
        <CallConfirmDialog
          open={showInlineDialog}
          number={number}
          onStartCall={handleStartCall}
          onCancel={handleCancel}
        />
      )}
    </>
  );
}

export function CallConfirmDialog({
  open,
  number,
  onStartCall,
  onCancel,
}: {
  open: boolean;
  number: string;
  onStartCall: () => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation('desktop_ui');
  const [dontShowAgain, setDontShowAgain] = React.useState(false);

  // Reset checkbox when dialog closes
  React.useEffect(() => {
    if (!open) {
      setDontShowAgain(false);
    }
  }, [open]);

  const handleStartCall = () => {
    if (dontShowAgain) {
      localStorage.setItem(CALL_CONFIRMATION_DISMISSED_KEY, 'true');
    }
    onStartCall();
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) {
          onCancel();
        }
      }}
    >
      <DialogContent
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.stopPropagation()}
        showCloseIcon={false}
        className="flex w-[360px] flex-col gap-0 overflow-hidden rounded-2xl border border-primary-10 bg-white p-0 shadow-lg"
      >
        <div className="flex flex-col gap-1 p-4">
          <DialogTitle className="text-heading-18 text-primary-100">
            {t('components.dialer.callConfirmation.title')}
          </DialogTitle>
          <DialogDescription className="text-body-12 text-primary-60">
            {t('components.dialer.callConfirmation.description', { number })}
          </DialogDescription>
          <div className="mt-2 flex items-center gap-2">
            <Checkbox
              id="call-confirm-dont-show"
              className="cursor-pointer"
              checked={dontShowAgain}
              onCheckedChange={(checked) => setDontShowAgain(checked === true)}
            />
            <label
              htmlFor="call-confirm-dont-show"
              className="cursor-pointer text-body-12 text-primary-60 select-none"
            >
              {t('components.dialer.callConfirmation.dontShowAgain')}
            </label>
          </div>
        </div>
        <DialogFooter className="flex-row justify-end border-t border-primary-5 p-3">
          <Button variant="outline" size="sm" onClick={onCancel}>
            {t('components.dialer.callConfirmation.cancel')}
          </Button>
          <Button variant="primary" size="sm" onClick={handleStartCall}>
            {t('components.dialer.callConfirmation.startCall')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Wrapper to isolate popover state from parent re-renders
function EditablePhoneCell({
  children,
  phoneNumbers,
  isOpen,
  onOpenChange,
  onPhoneAdd,
  onPhoneDelete,
  contactId,
  contactName,
  onRequestCallConfirm,
}: {
  children: React.ReactNode;
  phoneNumbers: string[];
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onPhoneAdd?: (phoneNumber: string) => void;
  onPhoneDelete?: (phoneNumber: string) => void;
  contactId?: string;
  contactName?: string;
  onRequestCallConfirm?: (startCall: () => void, number: string) => void;
}) {
  return (
    <PhonePopover
      phoneNumbers={phoneNumbers}
      open={isOpen}
      onOpenChange={onOpenChange}
      onAddPhone={onPhoneAdd}
      onDeletePhone={onPhoneDelete}
      contactId={contactId}
      contactName={contactName}
      onRequestCallConfirm={onRequestCallConfirm}
    >
      {children}
    </PhonePopover>
  );
}

export interface PhoneCellHandle {
  open: () => void;
}

export const PhoneCell = React.memo(
  React.forwardRef<
    PhoneCellHandle,
    {
      contactId?: string;
      contactName?: string;
      phoneNumbers?: string[];
      isOpen?: boolean;
      onOpenChange?: (open: boolean) => void;
      /** Stable callback - receives contactId and currentNumbers to avoid closures */
      onPhoneAdd?: (contactId: string, currentNumbers: string[], phoneNumber: string) => void;
      /** Stable callback - receives contactId and currentNumbers to avoid closures */
      onPhoneDelete?: (contactId: string, currentNumbers: string[], phoneNumber: string) => void;
    }
  >(function PhoneCell(
    {
      contactId,
      contactName,
      phoneNumbers,
      isOpen: controlledIsOpen,
      onOpenChange,
      onPhoneAdd,
      onPhoneDelete,
    },
    ref,
  ) {
    const [internalIsOpen, setInternalIsOpen] = React.useState(false);

    // Call confirmation dialog state — hoisted here so the Dialog renders
    // outside the PhonePopover tree and doesn't interfere with popover events
    const [confirmDialog, setConfirmDialog] = React.useState<{
      startCall: () => void;
      number: string;
    } | null>(null);

    const handleRequestCallConfirm = React.useCallback((startCall: () => void, number: string) => {
      setConfirmDialog({ startCall, number });
    }, []);

    const handleConfirmStartCall = () => {
      confirmDialog?.startCall();
      setConfirmDialog(null);
    };

    const handleConfirmCancel = () => {
      setConfirmDialog(null);
    };

    // Use controlled state if provided, otherwise use internal state
    const isControlled = controlledIsOpen !== undefined;
    const isOpen = isControlled ? controlledIsOpen : internalIsOpen;
    const setIsOpen = React.useCallback(
      (open: boolean) => {
        if (isControlled) {
          onOpenChange?.(open);
        } else {
          setInternalIsOpen(open);
        }
      },
      [isControlled, onOpenChange],
    );

    const multiple = phoneNumbers?.length ? phoneNumbers.length > 1 : false;

    React.useImperativeHandle(
      ref,
      () => ({
        open: () => {
          setIsOpen(true);
        },
      }),
      [setIsOpen],
    );

    // Wrap callbacks to include contactId and phoneNumbers for the stable parent callback
    const handlePhoneAdd = React.useCallback(
      (phone: string) => {
        if (contactId) {
          onPhoneAdd?.(contactId, phoneNumbers ?? [], phone);
        }
      },
      [contactId, phoneNumbers, onPhoneAdd],
    );

    const handlePhoneDelete = React.useCallback(
      (phone: string) => {
        if (contactId) {
          onPhoneDelete?.(contactId, phoneNumbers ?? [], phone);
        }
      },
      [contactId, phoneNumbers, onPhoneDelete],
    );

    const cellContent = (
      <TableCell
        className={cn(
          'overflow-hidden px-2',
          multiple && 'gap-1',
          multiple &&
            'group-focus:min-h-[65px] group-focus:max-h-[192px] group-focus:overflow-y-auto group-focus:flex-col group-focus:items-start group-focus:py-2.5',
        )}
      >
        {phoneNumbers?.map((phoneNumber) => (
          <Phone
            key={phoneNumber}
            phoneNumber={phoneNumber}
            contactId={contactId}
            contactName={contactName}
            onRequestCallConfirm={handleRequestCallConfirm}
          />
        ))}
      </TableCell>
    );

    // If no contactId, just render the cell without popover (read-only mode)
    if (!contactId) {
      return cellContent;
    }

    return (
      <>
        <EditablePhoneCell
          phoneNumbers={phoneNumbers ?? []}
          isOpen={isOpen}
          onOpenChange={setIsOpen}
          onPhoneAdd={handlePhoneAdd}
          onPhoneDelete={handlePhoneDelete}
          contactId={contactId}
          contactName={contactName}
          onRequestCallConfirm={handleRequestCallConfirm}
        >
          {cellContent}
        </EditablePhoneCell>
        <CallConfirmDialog
          open={confirmDialog !== null}
          number={confirmDialog?.number ?? ''}
          onStartCall={handleConfirmStartCall}
          onCancel={handleConfirmCancel}
        />
      </>
    );
  }),
);

export function PhonePopover({
  children,
  phoneNumbers,
  open: controlledOpen,
  onOpenChange,
  onReorder,
  onAddPhone,
  onDeletePhone,
  collisionBoundary,
  align = 'start',
  side,
  sideOffset = -41,
  contactId,
  contactName,
  onRequestCallConfirm,
  readOnly,
}: {
  children: React.ReactNode;
  phoneNumbers: string[];
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  onReorder?: (reorderedNumbers: string[]) => void;
  onAddPhone?: (phoneNumber: string) => void;
  onDeletePhone?: (phoneNumber: string) => void;
  collisionBoundary?: Element | Element[] | null;
  align?: 'start' | 'center' | 'end';
  side?: 'top' | 'right' | 'bottom' | 'left';
  sideOffset?: number;
  contactId?: string;
  contactName?: string;
  onRequestCallConfirm?: (startCall: () => void, number: string) => void;
  readOnly?: boolean;
}) {
  const triggerRef = React.useRef<HTMLDivElement>(null);
  const [internalOpen, setInternalOpen] = React.useState(false);

  // Support both controlled and uncontrolled modes
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : internalOpen;
  const setOpen = React.useCallback(
    (value: boolean) => {
      if (isControlled) {
        onOpenChange?.(value);
      } else {
        setInternalOpen(value);
      }
    },
    [isControlled, onOpenChange],
  );
  const [orderedNumbers, setOrderedNumbers] = React.useState<string[]>(phoneNumbers);
  const [selectedCountry, setSelectedCountry] = React.useState<CountryCode>('US');
  const [phoneInputValue, setPhoneInputValue] = React.useState('');
  const itemRefs = React.useRef<(HTMLDivElement | null)[]>([]);
  const countryTriggerRef = React.useRef<HTMLButtonElement>(null);
  const phoneInputRef = React.useRef<HTMLInputElement>(null);

  // Sync ordered numbers when phoneNumbers prop changes
  React.useEffect(() => {
    if (open) {
      setOrderedNumbers(phoneNumbers);
    }
  }, [open, phoneNumbers]);

  // Reset input value when popover closes
  const prevOpenRef = React.useRef(open);
  React.useEffect(() => {
    if (!open && prevOpenRef.current) {
      // Popover just closed - reset input
      setPhoneInputValue('');
    }
    prevOpenRef.current = open;
  }, [open]);

  const handleItemKeyDown = (e: React.KeyboardEvent<HTMLDivElement>, index: number) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      e.stopPropagation();
      if (index < orderedNumbers.length - 1) {
        // Move to next item
        itemRefs.current[index + 1]?.focus();
      } else if (!readOnly) {
        // At the last item, focus the country trigger (only in edit mode)
        countryTriggerRef.current?.focus();
      }
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      e.stopPropagation();
      if (index > 0) {
        // Move to previous item
        itemRefs.current[index - 1]?.focus();
      }
    }
  };

  const handleOpenChange = React.useCallback(
    (newOpen: boolean) => {
      // Only propagate if the state is actually changing - ignore spurious Radix calls during re-renders
      if (newOpen === open) {
        return;
      }
      setOpen(newOpen);
      // When closing the popover, restore focus to the original cell
      if (!newOpen) {
        // Find the focusable cell element (parent of the anchor)
        const cell = triggerRef.current?.closest('[tabindex]') as HTMLElement | null;
        if (cell) {
          // Use requestAnimationFrame to ensure focus happens after popover fully closes
          requestAnimationFrame(() => {
            cell.focus();
          });
        }
      }
    },
    [setOpen, open],
  );

  const onDragEnd = (result: DropResult) => {
    if (!result.destination) {
      return;
    }

    if (result.source.index === result.destination.index) {
      return;
    }

    const reordered = reorder(orderedNumbers, result.source.index, result.destination.index);
    setOrderedNumbers(reordered);
    onReorder?.(reordered);
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange} modal>
      <PopoverAnchor
        onDoubleClick={() => {
          setOpen(true);
        }}
        ref={triggerRef}
      >
        {children}
      </PopoverAnchor>
      <PopoverContent
        onEscapeKeyDown={(e) => e.stopPropagation()}
        align={align}
        side={side}
        sideOffset={sideOffset}
        collisionBoundary={collisionBoundary}
        className="z-popover w-[var(--radix-popover-trigger-width)] min-w-[280px] rounded-2xl bg-white"
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          if (readOnly) {
            // In readOnly mode, focus the first phone number
            const firstRow = itemRefs.current[0];
            const firstPhone = firstRow?.querySelector('span button') as HTMLElement;
            firstPhone?.focus();
          } else {
            phoneInputRef.current?.focus();
          }
        }}
        onKeyDown={(e) => {
          // Prevent Enter from closing the popover
          if (e.key === 'Enter') {
            e.preventDefault();
            e.stopPropagation();
          }
        }}
      >
        {orderedNumbers.length > 0 && (
          <PopoverScrollArea className="max-h-[192px]">
            <DragDropContext onDragEnd={onDragEnd}>
              <Droppable droppableId="phoneNumbersList">
                {(provided) => (
                  <div
                    {...provided.droppableProps}
                    ref={provided.innerRef}
                    className="relative pt-2.5 pr-[11px] pb-1.5 pl-2"
                  >
                    {orderedNumbers.map((phoneNumber, index) => (
                      <Draggable key={phoneNumber} draggableId={phoneNumber} index={index}>
                        {(provided, snapshot) => (
                          <div
                            ref={(node) => {
                              provided.innerRef(node);
                              itemRefs.current[index] = node;
                            }}
                            {...provided.draggableProps}
                            tabIndex={-1}
                            onKeyDown={(e) => handleItemKeyDown(e, index)}
                            style={{
                              ...provided.draggableProps.style,
                              left: 'auto !important',
                              top: 'auto !important',
                            }}
                            className={cn(
                              'group/row mb-1 flex select-none items-center gap-2 rounded-md px-1 -mx-1 outline-none',
                              snapshot.isDragging && 'opacity-50',
                            )}
                          >
                            {/* Drag handle - hidden until backend supports reordering */}
                            <div
                              {...provided.dragHandleProps}
                              tabIndex={-1}
                              className="hidden cursor-grab items-center justify-center outline-none active:cursor-grabbing"
                            >
                              <SixDots className="rotate-90" />
                            </div>

                            <span
                              className="mr-auto"
                              onKeyDown={(e) => {
                                if (readOnly) {
                                  if (e.key === 'Tab' && !e.shiftKey) {
                                    e.preventDefault();
                                    // In readOnly, skip bin — go to next row's phone
                                    const row = e.currentTarget.parentElement;
                                    const nextRow = row?.nextElementSibling;
                                    if (nextRow) {
                                      const nextPhone = nextRow.querySelector(
                                        'span button',
                                      ) as HTMLElement;
                                      nextPhone?.focus();
                                    }
                                  } else if (e.key === 'Tab' && e.shiftKey) {
                                    e.preventDefault();
                                    // In readOnly, skip bin — go to previous row's phone
                                    const row = e.currentTarget.parentElement;
                                    const prevRow = row?.previousElementSibling;
                                    if (prevRow) {
                                      const prevPhone = prevRow.querySelector(
                                        'span button',
                                      ) as HTMLElement;
                                      prevPhone?.focus();
                                    }
                                  }
                                } else {
                                  if (e.key === 'Tab' && !e.shiftKey) {
                                    e.preventDefault();
                                    // Focus the first button in the actions wrapper (next sibling)
                                    const wrapper = e.currentTarget
                                      .nextElementSibling as HTMLElement;
                                    wrapper?.querySelector<HTMLElement>('button')?.focus();
                                  } else if (e.key === 'Tab' && e.shiftKey) {
                                    e.preventDefault();
                                    // Focus previous row's bin, or wrap to phone input
                                    const row = e.currentTarget.parentElement;
                                    const prevRow = row?.previousElementSibling;
                                    if (prevRow) {
                                      const prevBin = prevRow.querySelector(
                                        'button:last-of-type',
                                      ) as HTMLElement;
                                      prevBin?.focus();
                                    } else {
                                      phoneInputRef.current?.focus();
                                    }
                                  }
                                }
                              }}
                            >
                              <Phone
                                phoneNumber={phoneNumber}
                                tabIndex={0}
                                contactId={contactId}
                                contactName={contactName}
                                onRequestCallConfirm={onRequestCallConfirm}
                                hideCopy
                              />
                            </span>
                            <div className="flex items-center gap-0.5">
                              <PopoverCopyButton phoneNumber={phoneNumber} />
                              {!readOnly && (
                                <button
                                  type="button"
                                  tabIndex={0}
                                  className="group/bin cursor-pointer p-1 text-primary-40 opacity-0 outline-none transition-opacity hover:text-extra-decline hover:opacity-100 focus:opacity-100 group-hover/row:opacity-100"
                                  onClick={() => onDeletePhone?.(phoneNumber)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Tab' && !e.shiftKey) {
                                      e.preventDefault();
                                      // Focus next row's phone, or country trigger
                                      const wrapper = e.currentTarget.parentElement;
                                      const row = wrapper?.parentElement;
                                      const nextRow = row?.nextElementSibling;
                                      if (nextRow) {
                                        const nextPhone = nextRow.querySelector(
                                          'span button',
                                        ) as HTMLElement;
                                        nextPhone?.focus();
                                      } else {
                                        countryTriggerRef.current?.focus();
                                      }
                                    } else if (e.key === 'Tab' && e.shiftKey) {
                                      e.preventDefault();
                                      // Focus the copy button (previous sibling)
                                      const copyButton = e.currentTarget
                                        .previousElementSibling as HTMLElement;
                                      copyButton?.focus();
                                    }
                                  }}
                                >
                                  <span className="flex rounded-xs group-focus/bin:ring-2 group-focus/bin:ring-primary-40">
                                    <Icon name={Icons.Bin} className="text-s" />
                                  </span>
                                </button>
                              )}
                            </div>
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          </PopoverScrollArea>
        )}
        {!readOnly && (
          <div
            className={cn('px-2 py-2.5', orderedNumbers.length > 0 && 'border-t border-primary-5')}
            onKeyDown={(e) => {
              // Handle Tab from phone input to wrap to first phone
              if (e.key === 'Tab' && !e.shiftKey && e.target === phoneInputRef.current) {
                if (orderedNumbers.length > 0) {
                  e.preventDefault();
                  // Focus the first phone in the list
                  const firstRow = itemRefs.current[0];
                  const firstPhone = firstRow?.querySelector('span button') as HTMLElement;
                  firstPhone?.focus();
                }
              }
              // Handle Shift+Tab from country trigger to go to last bin
              if (
                e.key === 'Tab' &&
                e.shiftKey &&
                e.target === countryTriggerRef.current &&
                orderedNumbers.length > 0
              ) {
                e.preventDefault();
                const lastRow = itemRefs.current[orderedNumbers.length - 1];
                const lastBin = lastRow?.querySelector('button:last-of-type') as HTMLElement;
                lastBin?.focus();
              }
            }}
          >
            <PhoneInput
              countryCode={selectedCountry}
              onCountryChange={setSelectedCountry}
              value={phoneInputValue}
              onChange={setPhoneInputValue}
              countryTriggerRef={countryTriggerRef}
              inputRef={phoneInputRef}
              onCountryNavigateUp={() => {
                // Focus the last item in the list
                const lastIndex = orderedNumbers.length - 1;
                if (lastIndex >= 0) {
                  itemRefs.current[lastIndex]?.focus();
                }
              }}
              onSubmit={(fullNumber) => {
                onAddPhone?.(fullNumber);
              }}
            />
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
